import { GLOBAL_REGION_CODES, getRegionMetadata, } from '../constants/regionMetadata.js';
function uniqueSorted(values) {
    return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b));
}
export function isGlobalRouting(input) {
    const regions = [input.sourceRegion, ...(input.destinationRegions ?? [])].filter((region) => typeof region === 'string' && region.trim().length > 0);
    return (input.scope === 'global' ||
        input.deploymentType === 'global-endpoint' ||
        (input.offeringId ?? '').startsWith('global.') ||
        regions.some((region) => GLOBAL_REGION_CODES.has(region.trim().toLowerCase())) ||
        // Some archived rows label regional profiles (apac.*) 'Global'; the label only
        // decides when no region is published.
        (input.regionalGroup === 'Global' && regions.length === 0));
}
export function deriveResidencyBoundary(input) {
    const globalRouting = isGlobalRouting(input);
    const codes = uniqueSorted([input.sourceRegion, ...(input.destinationRegions ?? [])]
        .filter((region) => typeof region === 'string' && region.trim().length > 0)
        .map((region) => region.trim())
        .filter((region) => !GLOBAL_REGION_CODES.has(region.toLowerCase())));
    const known = [];
    const unknownRegions = [];
    for (const code of codes) {
        const metadata = getRegionMetadata(code);
        if (metadata)
            known.push(metadata);
        else
            unknownRegions.push(code);
    }
    const regionJurisdictions = uniqueSorted(known.map((region) => region.jurisdiction));
    const countries = uniqueSorted(known.map((region) => region.country).filter((country) => Boolean(country)));
    const regionGroups = uniqueSorted(known.map((region) => region.regionGroup));
    const jurisdictions = globalRouting
        ? ['Global', ...regionJurisdictions]
        : codes.length === 0
            ? ['Unknown']
            : [...regionJurisdictions, ...(unknownRegions.length > 0 ? ['Unknown'] : [])];
    let boundary;
    if (globalRouting)
        boundary = 'global';
    else if (codes.length === 0 || unknownRegions.length > 0)
        boundary = 'unknown';
    else if (regionJurisdictions.length === 1)
        boundary = regionJurisdictions[0];
    else if (regionGroups.length === 1)
        boundary = regionGroups[0];
    else
        boundary = 'cross-border';
    return {
        boundary,
        staysWithinJurisdiction: !globalRouting && codes.length > 0 && unknownRegions.length === 0 && regionJurisdictions.length === 1,
        jurisdictions,
        countries,
        regionGroups,
        unknownRegions,
        globalRouting,
    };
}
function crossBoundaryRoutes(sourceRegion, destinationRegions) {
    if (!sourceRegion || !destinationRegions || destinationRegions.length === 0)
        return [];
    const source = getRegionMetadata(sourceRegion)?.jurisdiction ?? 'Unknown';
    const routes = new Set();
    for (const region of destinationRegions) {
        if (GLOBAL_REGION_CODES.has(region.trim().toLowerCase()))
            continue;
        const destination = getRegionMetadata(region)?.jurisdiction ?? 'Unknown';
        if (destination !== source)
            routes.add(`${source}->${destination}`);
    }
    return Array.from(routes).sort();
}
function strictRegionCode(code) {
    const platform = getRegionMetadata(code)?.platform;
    return platform === 'aws' || platform === 'gcp' || platform === 'azure';
}
/**
 * Return the offering's residency with boundary fields and region-derived
 * jurisdictions filled in. Scope, evidence and other stored fields are kept.
 */
export function withResidencyBoundary(offering) {
    const stored = offering.residency ?? {
        scope: 'single-region',
        jurisdictions: [],
        evidence: { confidence: 'inferred', summary: 'Derived from available region metadata.' },
    };
    const derived = deriveResidencyBoundary({
        offeringId: offering.offeringId,
        scope: stored.scope,
        deploymentType: offering.deployment.deploymentType,
        regionalGroup: offering.deployment.regionalGroup,
        sourceRegion: offering.deployment.sourceRegion,
        destinationRegions: offering.deployment.destinationRegions,
    });
    let assuranceClass = stored.assuranceClass;
    if (derived.boundary === 'unknown') {
        assuranceClass = 'unknown';
    }
    else if (assuranceClass === 'unknown' || assuranceClass === undefined) {
        if (derived.globalRouting || derived.boundary === 'cross-border')
            assuranceClass = 'global-routing';
        else if (stored.scope === 'single-region' && strictRegionCode(offering.deployment.sourceRegion)) {
            assuranceClass = 'strict-region';
        }
        else
            assuranceClass = 'geography-boundary';
    }
    const routes = derived.globalRouting
        ? []
        : crossBoundaryRoutes(offering.deployment.sourceRegion, offering.deployment.destinationRegions);
    const { crossBoundaryRoutes: _storedRoutes, ...rest } = stored;
    return {
        ...rest,
        jurisdictions: derived.jurisdictions,
        ...(routes.length > 0 ? { crossBoundaryRoutes: routes } : {}),
        ...(assuranceClass ? { assuranceClass } : {}),
        boundary: derived.boundary,
        staysWithinJurisdiction: derived.staysWithinJurisdiction,
        countries: derived.countries,
        regionGroups: derived.regionGroups,
    };
}
/** True when every possible processing region is inside the given jurisdiction. */
export function staysWithin(residency, jurisdiction) {
    return Boolean(residency?.staysWithinJurisdiction && residency.boundary === jurisdiction);
}
/** Boundary filter values offered in UIs, in display order. */
export const RESIDENCY_BOUNDARY_FILTERS = [
    { value: 'EU', label: 'EU only' },
    { value: 'UK', label: 'UK only' },
    { value: 'Europe', label: 'Europe (EU, UK and other European regions)' },
    { value: 'US', label: 'US only' },
    { value: 'Canada', label: 'Canada only' },
    { value: 'Japan', label: 'Japan only' },
    { value: 'Australia', label: 'Australia only' },
    { value: 'APAC', label: 'Asia Pacific' },
    { value: 'global', label: 'Global routing' },
    { value: 'unknown', label: 'Region not published' },
];
/**
 * Whether an offering matches a boundary filter value. A jurisdiction value
 * ('EU') matches only offerings that stay inside it; a region-group value
 * ('Europe') also matches offerings that stay inside one of its jurisdictions.
 */
export function matchesBoundaryFilter(residency, filter) {
    if (!residency?.boundary)
        return false;
    if (residency.boundary === filter)
        return true;
    const groups = residency.regionGroups ?? [];
    return (groups.length === 1 &&
        groups[0] === filter &&
        residency.boundary !== 'global' &&
        residency.boundary !== 'cross-border' &&
        residency.boundary !== 'unknown');
}
//# sourceMappingURL=residencyBoundary.js.map